import { parseMessageData } from './utils';
export const WithLatencyMeasurement = (Base) => {
    return class extends Base {
        #measurementInterval;
        #measurementEnabledForNextSegment = false;
        #latencyMeasurementTimeout = null;
        #segmentsPlayerDownloadTime = {};
        #segmentsMeasureData = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        constructor(...args) {
            super(...args);
            const options = args[0];
            this.#measurementInterval = options?.latencyMeasurementInterval;
        }
        load(request) {
            if (!this.#isMeasurementEnabled()) {
                return super.load(request);
            }
            this.#startLatencyMeasurement();
            return super.load(request);
        }
        #isMeasurementEnabled() {
            return Boolean(this.#measurementInterval && this.#measurementInterval > 0);
        }
        #startLatencyMeasurement() {
            this.#measurementEnabledForNextSegment = true;
        }
        onSegmentRequestFinished(data) {
            super.onSegmentRequestFinished(data);
            if (!this.#isMeasurementEnabled()) {
                return;
            }
            this.#segmentsPlayerDownloadTime[data.segmentId] = { ...data };
        }
        onEmsgMetadata(data) {
            super.onEmsgMetadata(data);
            if (!this.#isMeasurementEnabled() || !this.#measurementEnabledForNextSegment) {
                return;
            }
            const presentationTimeId = String(data.presentationTime);
            this.#segmentsMeasureData[presentationTimeId] = {
                ...this.#segmentsMeasureData[presentationTimeId],
                emsgMetadata: data,
            };
            this.#collectLatencyData(presentationTimeId);
        }
        onSegmentPlayback(data) {
            super.onSegmentPlayback(data);
            if (!this.#isMeasurementEnabled() || !this.#measurementEnabledForNextSegment) {
                return;
            }
            const presentationTimeId = String(data.presentationTime);
            this.#segmentsMeasureData[presentationTimeId] = {
                ...this.#segmentsMeasureData[presentationTimeId],
                segmentPlayback: data,
            };
            this.#collectLatencyData(presentationTimeId);
        }
        #latencyMeasurementError(error) {
            // full cleanup
            this.#segmentsPlayerDownloadTime = {};
            this.#restartLatencyMeasurement();
            this.logger?.error(`Latency measurement error: ${error}`);
        }
        #cleanupSegmentsPlayerDownloadTime(olderThan) {
            Object.entries(this.#segmentsPlayerDownloadTime).forEach(([segmentId, segmentData]) => {
                if (segmentData.playerDownloaded < olderThan) {
                    delete this.#segmentsPlayerDownloadTime[segmentId];
                }
            });
        }
        #restartLatencyMeasurement() {
            this.#measurementEnabledForNextSegment = false;
            this.#segmentsMeasureData = {};
            if (this.#latencyMeasurementTimeout) {
                clearTimeout(this.#latencyMeasurementTimeout);
            }
            this.#latencyMeasurementTimeout = setTimeout(() => {
                this.#measurementEnabledForNextSegment = true;
            }, (this.#measurementInterval ?? 0) * 1000);
        }
        #collectLatencyData(presentationTimeId) {
            const segmentData = this.#segmentsMeasureData[presentationTimeId];
            if (!segmentData ||
                !segmentData.emsgMetadata ||
                !segmentData.segmentPlayback) {
                // latency data not ready yet
                return;
            }
            const segmentId = segmentData.segmentPlayback.segmentId;
            const segmentDownloadTimeData = this.#segmentsPlayerDownloadTime[segmentId];
            if (!segmentDownloadTimeData) {
                // shouldn't happen, as download happens before playback
                this.#latencyMeasurementError('Segment download time not found');
                return;
            }
            const downloadEnd = segmentDownloadTimeData.playerDownloaded;
            const emsgMetadata = segmentData.emsgMetadata.emsgData;
            const playerPlayedTime = segmentData.segmentPlayback.playerPlayed;
            const messageData = parseMessageData(emsgMetadata);
            if (!messageData) {
                this.#latencyMeasurementError('Invalid EMSG metadata');
                return;
            }
            const latencyMeasurementData = {
                ...messageData,
                downloadEnd,
                time: playerPlayedTime,
            };
            this.dispatchLatencyChanged(latencyMeasurementData);
            this.#cleanupSegmentsPlayerDownloadTime(downloadEnd);
            this.#restartLatencyMeasurement();
        }
    };
};
